import { createContext, useContext, useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { count as queueCount, syncPending } from "@/lib/offlineQueue";
import { toast } from "sonner";

const OfflineSyncContext = createContext({ pending: 0, online: true, sync: async () => {}, refresh: async () => {} });

export function OfflineSyncProvider({ children }) {
  const [pending, setPending] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => { setPending(await queueCount()); }, []);

  const sync = useCallback(async () => {
    if (!navigator.onLine || busy) return;
    if ((await queueCount()) === 0) return;
    setBusy(true);
    const ok = await syncPending(api);
    await refresh();
    setBusy(false);
    if (ok > 0) {
      toast.success(`${ok} offline erfasste Komponente(n) synchronisiert`);
      window.dispatchEvent(new Event("csp-sync-done"));
    }
  }, [busy, refresh]);

  useEffect(() => {
    refresh();
    const onOnline = () => { setOnline(true); sync(); };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("csp-queue-changed", refresh);
    const t = setInterval(() => { refresh(); if (navigator.onLine) sync(); }, 20000);
    if (navigator.onLine) sync();
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("csp-queue-changed", refresh);
      clearInterval(t);
    };
  }, [refresh, sync]);

  return (
    <OfflineSyncContext.Provider value={{ pending, online, sync, refresh, busy }}>
      {children}
    </OfflineSyncContext.Provider>
  );
}

export function useOfflineSync() {
  return useContext(OfflineSyncContext);
}
